import { Avatar, Button, makeStyles, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { KeyboardArrowDown, Logout } from '@material-symbols-svg/react/rounded/w500';
import { useMemo, useState } from 'react';

import { firstNameOf, initialsOf } from '../common';
import { useCurrentUser } from '../current-user';
import { useSettings } from '../settings';
import { palette, tokens } from '../theme';

export const ProfileChip: React.FC = () => {
    const classes = useStyles();
    const { user } = useCurrentUser();
    const { logout } = useSettings();
    const [open, setOpen] = useState(false);

    const initials = useMemo(() => (user ? initialsOf(user.displayName) : '?'), [user]);
    const firstName = useMemo(() => (user ? firstNameOf(user.displayName) : ''), [user]);

    if (!user) {
        return <div className={classes.placeholder} aria-hidden />;
    }

    const handleLogout = (): void => {
        setOpen(false);
        logout();
    };

    return (
        <Popover positioning="below-end" withArrow open={open} onOpenChange={(_, data) => setOpen(data.open)}>
            <PopoverTrigger disableButtonEnhancement>
                <Button
                    appearance="subtle"
                    aria-label={`Signed in as ${user.displayName}`}
                    className={classes.trigger}
                    icon={
                        <Avatar
                            size={24}
                            name={user.displayName}
                            initials={initials}
                            image={user.avatarObjectUrl ? { src: user.avatarObjectUrl } : undefined}
                        />
                    }
                    iconPosition="before"
                >
                    <span className={classes.firstName}>{firstName}</span>
                    <KeyboardArrowDown className={classes.chevron} aria-hidden />
                </Button>
            </PopoverTrigger>
            <PopoverSurface className={classes.surface}>
                <div className={classes.identity}>
                    <Avatar
                        size={56}
                        name={user.displayName}
                        initials={initials}
                        image={user.avatarObjectUrl ? { src: user.avatarObjectUrl } : undefined}
                    />
                    <div className={classes.identityText}>
                        <span className={classes.displayName}>{user.displayName}</span>
                        <span className={classes.email}>{user.email}</span>
                    </div>
                </div>
                <div className={classes.divider} aria-hidden />
                <button type="button" className={classes.menuItem} onClick={handleLogout}>
                    <Logout className={classes.menuIcon} />
                    <span>Sign out</span>
                </button>
            </PopoverSurface>
        </Popover>
    );
};

const useStyles = makeStyles({
    trigger: {
        gap: tokens.spacingHorizontalS,
    },
    firstName: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    chevron: {
        width: '16px',
        height: '16px',
        color: tokens.colorNeutralForeground3,
    },
    placeholder: {
        width: '64px',
        height: '32px',
    },
    surface: {
        padding: 0,
        minWidth: '260px',
    },
    identity: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    },
    identityText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    displayName: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    email: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    divider: {
        height: '1px',
        backgroundColor: palette.cardBorder,
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        width: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        textAlign: 'left',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: palette.panelMuted,
        },
    },
    menuIcon: {
        width: '18px',
        height: '18px',
        color: tokens.colorNeutralForeground2,
    },
});
